/**
 * NEXORA AI - ValidationMessage Component (Design System)
 * Epic 1.3 - Onboarding Premium
 * 
 * Composant générique de feedback utilisateur
 * Utilisé dans : Onboarding, Formulaires, Actions utilisateur, Notifications inline
 * 
 * API Extensible :
 * - Types : success, error, warning, info
 * - Modes : temporaire (auto-dismiss) ou persistant
 * - Animation configurable
 * - Icône personnalisable
 * - Action button optionnel
 */

import React, { useEffect } from 'react';
import { Check, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * ValidationMessage Props
 * 
 * @param {boolean} show - Contrôle l'affichage
 * @param {function} onClose - Callback de fermeture
 * @param {string} type - Type de message : 'success' | 'error' | 'warning' | 'info'
 * @param {string} message - Message principal
 * @param {string} description - Description additionnelle (optionnel)
 * @param {boolean} persistent - Message persistant (ne disparaît pas automatiquement)
 * @param {number} duration - Durée d'affichage en ms (défaut: 3000)
 * @param {boolean} dismissible - Afficher bouton de fermeture
 * @param {ReactNode} icon - Icône personnalisée (remplace l'icône par défaut)
 * @param {string} actionLabel - Label du bouton d'action (optionnel)
 * @param {function} onAction - Callback du bouton d'action
 * @param {string} position - Position : 'top' | 'center' | 'bottom'
 * @param {boolean} fullWidth - Prendre toute la largeur disponible
 * @param {string} className - Classes supplémentaires
 */
export default function ValidationMessage({
  show = false,
  onClose,
  type = 'success',
  message,
  description = null,
  persistent = false,
  duration = 3000,
  dismissible = false,
  icon = null,
  actionLabel = null,
  onAction = null,
  position = 'center',
  fullWidth = false,
  className = '',
}) {
  // Auto-dismiss pour messages temporaires
  useEffect(() => {
    if (show && !persistent && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, persistent, duration, onClose]);

  // Configuration des types
  const typeConfig = {
    success: {
      icon: Check,
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      iconBg: 'bg-green-500',
      text: 'text-green-400',
      buttonBg: 'bg-green-600 hover:bg-green-500',
    },
    error: {
      icon: AlertCircle,
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      iconBg: 'bg-red-500',
      text: 'text-red-400',
      buttonBg: 'bg-red-600 hover:bg-red-500',
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/30',
      iconBg: 'bg-yellow-500',
      text: 'text-yellow-400',
      buttonBg: 'bg-yellow-600 hover:bg-yellow-500',
    },
    info: {
      icon: Info,
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      iconBg: 'bg-blue-500',
      text: 'text-blue-400',
      buttonBg: 'bg-blue-600 hover:bg-blue-500',
    },
  };

  const config = typeConfig[type];
  const IconComponent = icon || config.icon;

  // Position
  const positionClasses = {
    top: 'justify-start',
    center: 'justify-center',
    bottom: 'justify-end',
  };

  // Animation variants
  const containerVariants = {
    hidden: { 
      opacity: 0, 
      y: position === 'top' ? -10 : position === 'bottom' ? 10 : 0,
      scale: 0.95 
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2 },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: { type: 'spring', duration: 0.4 },
    },
  };

  return (
    <AnimatePresence>
      {show && (
        <div className={cn('flex', positionClasses[position])}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              'p-4 rounded-xl border flex items-start gap-3',
              config.bg,
              config.border,
              fullWidth ? 'w-full' : 'max-w-md',
              className
            )}
          >
            {/* Icône avec animation spring */}
            <motion.div
              variants={iconVariants}
              initial="hidden"
              animate="visible"
              className={cn(
                'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0',
                config.iconBg
              )}
            >
              <IconComponent className="w-3 h-3 text-white" strokeWidth={3} />
            </motion.div>

            {/* Contenu */}
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium', config.text)}>
                {message}
              </p>
              {description && (
                <p className="text-xs text-zinc-400 mt-1">
                  {description}
                </p>
              )}

              {/* Bouton d'action (optionnel) */}
              {actionLabel && onAction && (
                <button
                  onClick={onAction}
                  className={cn(
                    'mt-3 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors',
                    config.buttonBg
                  )}
                >
                  {actionLabel}
                </button>
              )}
            </div>

            {/* Bouton de fermeture (optionnel) */}
            {dismissible && onClose && (
              <button
                onClick={onClose}
                className={cn(
                  'p-1 rounded-lg hover:bg-zinc-800 transition-colors flex-shrink-0',
                  config.text
                )}
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/**
 * ValidationMessageInline - Version inline (pas de wrapper flex)
 * Utile pour afficher dans un formulaire inline
 */
export function ValidationMessageInline(props) {
  return (
    <AnimatePresence>
      {props.show && (
        <motion.div
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
        >
          <ValidationMessage {...props} position="top" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * useValidationMessage - Hook pour gérer l'état d'un message de validation
 * 
 * @returns {object} { show, type, message, showMessage, hideMessage }
 * 
 * @example
 * const validation = useValidationMessage();
 * validation.showMessage('success', 'Enregistré avec succès');
 * <ValidationMessage {...validation} onClose={validation.hideMessage} />
 */
export function useValidationMessage() {
  const [state, setState] = React.useState({
    show: false,
    type: 'success',
    message: '',
    description: null,
  });

  const showMessage = (type, message, description = null) => {
    setState({ show: true, type, message, description });
  };

  const hideMessage = () => {
    setState((prev) => ({ ...prev, show: false }));
  };

  return {
    ...state,
    showMessage,
    hideMessage,
  };
}
